import React, { useContext, useRef, useState, useEffect } from "react";
import { NotFound, BugTab, Loading } from "@peterfosso/prairi-components";
import { useNavigate, useParams } from "react-router-dom";
import { navBarContext, authContext } from "./context/context";
import { sendBugEmails, updateLatestActions } from "../utils";
import { useMediaQuery } from "react-responsive";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase/firebaseApp";
import GreenTick from "../assets/lightergreentick.png";
import SilverDot from "../assets/silver-dot.png";
import usePagination from "@mui/material/usePagination/usePagination";
import Logo from "../assets/prairiwhite.png";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import axios from "axios";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { getFunctions, httpsCallable } from "firebase/functions";

const Password = () => {
  const { navBarHeight } = useContext(navBarContext);
  const { currentUser } = useContext(authContext);
  let navigate = useNavigate();
  const containerRef = useRef(null);
  const [screenshotUrl, setScreenshotUrl] = useState(""); //for bug report
  const [bugEmail, setBugEmail] = useState("");
  const [bugText, setBugText] = useState(null);
  const [bugError, setBugError] = useState(null);
  let { docId } = useParams();
  
  // Initialize Firebase Functions
  const functions = getFunctions();
  
  // Password state and validation
  const [data, setData] = useState({
    email: "", // Add email field to state
    password: "",
    confirmPassword: "",
    emailError: false, // Add email error field
    passwordError: false,
    confirmPasswordError: false,
    error: "",
    loading: false,
    message: "",
    letMeKnow: true,
  });

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const [passwordCharacters, setPasswordCharacters] = useState(false);
  const [passwordNumbers, setPasswordNumbers] = useState(false);
  const [passwordSpecial, setPasswordSpecial] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false); // Add loading state for email
  const [userData, setUserData] = useState(null);
  // Function to fetch user email from cloud function
  // This function calls the getUserEmail cloud function to retrieve the user's email
  const fetchUserData = async () => {
    setEmailLoading(true); // Start loading

    try {
      // Create a callable function reference to the getUserEmail cloud function
      const getUserDataFunction = httpsCallable(functions, 'getUserData');
      
      // Call the cloud function with the userId (docId from URL params)
      const result = await getUserDataFunction({ userId: docId });
      
      // Extract the user data from the response
      // Cloud functions return data in result.data
      const { userData } = result.data;
      
      // Update the state with the fetched user data
      setUserData(userData);
      
      // Pre-fill the email field if userData contains email
      if (userData && userData.email) {
        setData(prevData => ({
          ...prevData,
          email: userData.email
        }));
      }
      
      console.log("User data fetched successfully:", userData);
    } catch (error) {
      console.error("Error fetching user data:", error);
      // Handle error - you might want to show a user-friendly message
      setData(prevData => ({
        ...prevData,
        error: "Failed to load user data. Please try again."
      }));
    } finally {
      setEmailLoading(false); // End loading regardless of success/failure
    }
  };

  // Fetch user email when component mounts
  // This useEffect runs once when the component is first rendered
  useEffect(() => {
    if (docId) {
      fetchUserData();
    }
  }, [docId]); // Remove userData from dependencies to avoid infinite loop

  // Password validation effect
  useEffect(() => {
    if (data.password && data.password.length > 0) {
      setPasswordCharacters(data.password.length >= 8);
      setPasswordNumbers(/\d/.test(data.password));
      setPasswordSpecial(/[!@#$%^&*(),.?":{}|<>]/.test(data.password));
    } else {
      setPasswordCharacters(false);
      setPasswordNumbers(false);
      setPasswordSpecial(false);
    }
  }, [data.password]);

  // Check if passwords match
  useEffect(() => {
    if (data.confirmPassword && data.password) {
      setPasswordsMatch(data.password === data.confirmPassword);
    } else {
      setPasswordsMatch(false);
    }
  }, [data.password, data.confirmPassword]);

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const isValid = (fieldName) => {
    switch (fieldName) {
      case "email":
        // Email validation - check if it's a valid email format
        const emailValid = data.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email);
        setData({ ...data, emailError: !emailValid });
        break;
      case "password":
        const passwordValid = data.password.length >= 8 &&
          /\d/.test(data.password) &&
          /[!@#$%^&*(),.?":{}|<>]/.test(data.password);
        setData({ ...data, passwordError: !passwordValid });
        break;
      case "confirmPassword":
        const confirmValid = data.confirmPassword === data.password && data.confirmPassword.length > 0;
        setData({ ...data, confirmPasswordError: !confirmValid });
        break;
      default:
        break;
    }
  };

  const isFinallyValid = () => {
    let dataCopy = { ...data };
  
    // Validate email
    if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      dataCopy = {
        ...dataCopy,
        emailError: true,
        error: "Please provide a valid email address",
      };
      setData({ ...dataCopy });
      return false;
    }
  
    // Validate password
    if (data.password.length < 8 ||
        !/\d/.test(data.password) ||
        !/[!@#$%^&*(),.?":{}|<>]/.test(data.password)
    ) {
      dataCopy = {
        ...dataCopy,
        passwordError: true,
        error: "Password must be at least 8 characters long and include at least one number and one special character",
      };
    } else {
      dataCopy = {
        ...dataCopy,
        passwordError: false,
        error: ""
      };
    }

  
    setData({ ...dataCopy });
  
    const { passwordError, emailError } = dataCopy;
  
    return !(passwordError || emailError);
  };

  // Add a function to check if form is valid for button styling
  const isFormValid = () => {
    const emailValid = data.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email);
    const passwordValid = data.password.length >= 8 &&
      /\d/.test(data.password) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(data.password);
    
    return emailValid && passwordValid && !data.loading;
  };

  useEffect(() => {
    window.scrollTo(0, 0);

  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const valid = isFinallyValid();
    
    if (valid) {
      setData({
        ...data,
        error: "",
        loading: true,
        message: "Almost done...",
      });
      try{
        const response = await axios.post(
          "https://us-central1-prairi-prod-fe3a4.cloudfunctions.net/registerNewUser", 
          {
            userId: docId,
            password: data.password,
            letMeKnow: data.letMeKnow, // Include the letMeKnow preference
            email: data.email
          }
        );
        console.log("response", response);
        if (response.data.success) {
          setData({
            ...data,
            userId: response.data.userId,
            verified: true,
            loading: false,
            message: "Redirecting...",
          });
        }
        if (response.data.success && response.data.token) {
          const auth = getAuth();
    
          // Sign in using the custom token
          await signInWithCustomToken(auth, response.data.token);
    
          // Redirect based on category from userData
          if (userData && userData.category === "investor") {
            navigate("/investorexpress");
          } else if (userData && userData.category === "startup") {
            navigate("/startupregistration");
          } else {
            // Fallback navigation if category is not available
            console.warn("User category not found, redirecting to default");
            navigate("/startupregistration");
          }
        } else {
          throw new Error(response.data.error || "Registration failed.");
        } 
      } catch (error) {
        console.error("Error creating password:", error);
        setData({
          ...data,
          error: "An error occurred. Please double check your email and try again.",
        });
      }
    }
  };

  const handleUpload = (e) => {
    return new Promise((resolve, reject) => {
      const selectedFile = e.target.files[0];

      const storageRef = ref(storage, `bugs/${selectedFile.name}`);
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);

      uploadTask.on(
        "state_changed",
        null, // Manejador de progreso (puedes dejarlo como `null` si no necesitas reportarlo)
        (error) => {
          // Manejador de error
          console.error("Upload failed:", error);
          reject(error);
        },
        async () => {
          // Manejador de éxito
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
          console.log("uploaded correctly");
          setScreenshotUrl(downloadURL);
        }
      );
    });
  };

  const validateFields = () => {
    if (!bugText || bugText.trim() === "") {
      setBugError("Please describe the bug.");
      return false;
    }
    if (!bugEmail || bugEmail.trim() === "") {
      setBugError("We need your email address.");
      return false;
    }
    return true;
  };

  const handleBugText = (e) => {
    const value = e.target.value;
    setBugText(value);
    if (value !== "") {
      setBugError("");
    }
  };

  const handleBugEmail = (e) => {
    const value = e.target.value;
    setBugEmail(value);
    if (value !== "") {
      setBugError("");
    }
  };

  const handleUploadAndSendEmail = async (file) => {
    try {
      if (!validateFields()) return;

      let uploadedUrl = screenshotUrl;
      if (file) {
        uploadedUrl = await handleUpload({ target: { files: [file] } });
      }

      console.log(`screenshotUrl ${uploadedUrl}`);
      sendBugEmails(bugEmail, "", bugText, "Landing page", uploadedUrl || "");
    } catch (error) {
      console.error("Error uploading file or sending email:", error);
    }
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <section className="bg-lastSection bg-cover bg-right-bottom w-full h-screen relative py-2">
      <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      <div
        className={`py-4 from-navStart to-navEnd px-4 bg-black shadow-lg flex flex-col mx-auto relative z-10 justify-center items-center ${isMobile? "w-[80%]" : "w-1/3"}`}
        ref={containerRef}
        style={{ marginTop: navBarHeight + 20, borderRadius: "30px" }}
      >
        <BugTab
          email={bugEmail}
          setEmail={handleBugEmail}
          setText={handleBugText}
          submit={(file) => handleUploadAndSendEmail(file)}
          error={bugError}
          handleFileChange={handleUpload}
        />
        <div className="flex flex-col w-full items-start p-4" >
          {emailLoading ? (
            <div className="flex justify-center items-center w-full h-full">
              <Loading />
            </div>
          ) : (
            <>
              <img src={Logo} alt="logo" className="w-16 h-16 self-center" />
              <h1 className="text-white text-3xl text-left justify-left font-bold mt-8">Create your account</h1>

              <form onSubmit={handleSubmit} className="w-full">
                <div className="mt-5">
                  <label htmlFor="email" className="text-white">
                    Business Email

                  </label>
                  <div className={`flex items-center justify-between ${
                    data.emailError ? "border-red-500" : "border-blackish"
                  } bg-gray-100 mt-2.5 w-full rounded border p-2.5 text-black`}>
                    <input
                      className="outline-none w-full bg-transparent"
                      type="email"
                      name="email"
                      id="email"
                      value={data.email}
                      onChange={handleChange}
                      onBlur={() => isValid("email")}
                      placeholder="Business Email"
                      disabled={emailLoading}
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="password" className="text-white">
                    Password
                  </label>
                  <div className={`flex items-center justify-between ${
                    data.passwordError ? "border-red-500" : "border-blackish"
                  } bg-white mt-2.5 w-full rounded border p-2.5 text-black`}>
                    <input
                      className="outline-none w-full"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      id="password"
                      placeholder="Password"
                      value={data.password}
                      onChange={handleChange}
                      onBlur={() => isValid("password")}
                    />
                    <div className="cursor-pointer text-gray-500">
                      {showPassword ? (
                        <AiFillEyeInvisible onClick={togglePassword} />
                      ) : (
                        <AiFillEye onClick={togglePassword} />
                      )}
                    </div>
                  </div>

                </div>
                <div className="text-[12px] mt-4 italic text-white">
                    <div className="flex items-center">
                      <img
                        alt="bullet"
                        src={passwordCharacters ? GreenTick : SilverDot}
                        className={`${
                          passwordCharacters ? "w-[15px]" : "w-[10px]"
                        }  mr-2`}
                      />
                      <div>At least 8 characters</div>
                    </div>
                    <div className="flex items-center">
                      <img
                        alt="bullet"
                        src={passwordNumbers ? GreenTick : SilverDot}
                        className={`${
                          passwordNumbers ? "w-[15px]" : "w-[10px]"
                        }  mr-2`}
                      />
                      <div>At least one number</div>
                    </div>
                    <div className="flex items-center">
                      <img
                        alt="bullet"
                        src={passwordSpecial ? GreenTick : SilverDot}
                        className={`${
                          passwordSpecial ? "w-[15px]" : "w-[10px]"
                        }  mr-2`}
                      />
                      <div>At least one special character</div>
                    </div>

                  </div>
                  {data.passwordError && (
                    <div className="text-sm text-red-500 mt-4">
                      Password must be at least 8 characters long and include at least
                      one number and one special character
                    </div>
                  )}

                <div className="text-center mt-6 w-full">
                  <input
                    type="submit"
                    className={`px-4 py-3 border-gray-500 border text-md font-bold outline-none w-full cursor-pointer ${
                      isFormValid() ? "bg-greenPrairi/70" : "bg-[#a0a0a0]"
                    }`}
                    value="Create PRAIRI Account"
                    style={{
                      borderRadius: "5px",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  />
                </div>
                {userData?.category === "startup" && <div className="flex justify-center w-[80%] mx-auto bg-[#262626] p-2 rounded-lg items-center mt-8">
                    <label htmlFor="letMeKnow" className="flex items-start cursor-pointer select-none relative">
                      <input
                        type="checkbox"
                        id="letMeKnow"
                        name="letMeKnow"
                        checked={data.letMeKnow}
                        onChange={e => setData({ ...data, letMeKnow: e.target.checked })}
                        className="hidden"
                      />
                      <span
                        className="flex items-center justify-center mr-4"
                        style={{
                          width: 20,
                          height: 20,
                          border: '1px solid #f2f2f2',
                          borderRadius: 3,
                          transition: 'background 0.2s',
                          overflow: 'visible',
                          zIndex: 1,
                          flexShrink: 0,
                        }}
                      >
                        {data.letMeKnow && (
                          <img
                            src={GreenTick}
                            alt="checked"
                            style={{
                              width: 20,
                              height: 'auto',
                              position: 'absolute',
                              left: '3%',
                              top: '22%',
                              transform: 'translate(-50%, -50%)',
                              zIndex: 50,
                              pointerEvents: 'none',
                            }}
                          />
                        )}
                      </span>
                      <span className="text-white text-[10px]">Let me know about the latest investor and startup news, funding tips, exclusive events, webinars, and more.</span>
                    </label>
                  </div>}
              </form>

              {data.error && (
                <p className="mt-4 text-lg text-center text-red-500">
                  {data.error}
                </p>
              )}
              {data.message && (
                <div className="flex justify-center items-center my-2">
                  <span className="text-greenPrairi">{data.message}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default Password;
